import { useAuth } from "@ssf/auth-components";
import type { Prettify, WithPartial } from "@ssf/components";
import { CheckValuesType, DocumentType } from "@ssf/memdb-api";
import { useMemo } from "react";
import type { UseQueryClub } from "../lib/api.js";

const defaultFilter = [
  CheckValuesType.Statutes,
  CheckValuesType.ConstituentMeeting,
  CheckValuesType.AnnualMeeting,
  CheckValuesType.AnnualReport,
  CheckValuesType.ClassList,
  CheckValuesType.Members,
];

const DocumentTypCheckValueseMap = {
  [DocumentType.Statutes]: CheckValuesType.Statutes,
  [DocumentType.AnnualMeeting]: CheckValuesType.AnnualMeeting,
  [DocumentType.AnnualReport]: CheckValuesType.AnnualReport,
  [DocumentType.Membership]: CheckValuesType.Members,
  [DocumentType.ConstituentMeeting]: CheckValuesType.ConstituentMeeting,
  [DocumentType.ClassList]: CheckValuesType.ClassList,
} satisfies Record<number, CheckValuesType>;

const getId = (year: number, type: CheckValuesType) => `${year}.${type}`;

export type CheckValuesWithFile = Prettify<
  { id: string; pendingApproval: boolean; fileUrl?: string } & WithPartial<
    UseQueryClub["checkvalues"][0],
    "approved" | "signed" | "documentid" | "updated"
  >
>;

export const useDocuments = (
  club: Pick<UseQueryClub, "checkvalues" | "files">,
  filter = defaultFilter,
) => {
  const { user } = useAuth();
  return useMemo(() => {
    const documents = new Map<string, CheckValuesWithFile>();
    for (const checkvalue of club.checkvalues) {
      if (filter.includes(checkvalue.referenceid)) {
        const id = getId(checkvalue.year, checkvalue.referenceid);
        documents.set(id, {
          id,
          pendingApproval:
            !checkvalue.approved &&
            (checkvalue.signed || !checkvalue.documentid),
          ...checkvalue,
        });
      }
    }
    for (const file of club.files) {
      const referenceid = DocumentTypCheckValueseMap[file.documentType];
      const id = getId(file.documentYear, referenceid);
      const fileUrl = `${process.env.MEMDB_URL}/${file.path}?Authorization=Bearer ${user?.access_token}`;
      documents.set(id, {
        id,
        pendingApproval: false,
        year: file.documentYear,
        referenceid,
        fileUrl,
        updated: file.uploadDate,
        ...documents.get(id),
      });
    }
    return [...documents.values()].sort(
      (a, b) =>
        b.year - a.year || ((a.updated ?? "") < (b.updated ?? "") ? 1 : -1),
    );
  }, [club, filter, user?.access_token]);
};

export type ClubDocument = ReturnType<typeof useDocuments>[0];
